// ReportBugDrawer.tsx
import React, {
  useState,
  DragEvent,
  ChangeEvent,
  FormEvent,
  useEffect,
} from "react";
import { useReportBug } from "../../hooks/useReportBug";
import { message } from "antd";
import "./ReportBugDrawer.css";

interface FormData {
  name: string;
  email: string;
  description: string;
  screenshot: File | null;
}

interface ReportBugDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

const ReportBugDrawer: React.FC<ReportBugDrawerProps> = ({
  isOpen,
  onClose,
}) => {
  const {
    mutate: submitBugReport,
    isLoading,
    error,
    data,
    reset,
  } = useReportBug();

  const [formData, setFormData] = useState<FormData>({
    name: "",
    email: "",
    description: "",
    screenshot: null,
  });

  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [showUploadArea, setShowUploadArea] = useState<boolean>(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [filePreview, setFilePreview] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setFormData({
        name: "",
        email: "",
        description: "",
        screenshot: null,
      });
      setShowUploadArea(false);
      reset();
    }
  }, [isOpen, reset]);

  useEffect(() => {
    if (data) {
      message.success(data.message || "Bug report submitted successfully!");

      setTimeout(() => {
        handleCancel();
      }, 2000);
    }
  }, [data]);

  useEffect(() => {
    if (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to submit bug report";
      message.error(errorMessage);
    }
  }, [error]);

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        message.error(
          "File size exceeds 10MB limit. Please choose a smaller file.",
        );
        return;
      }
      const validTypes = ["image/png", "image/jpeg", "image/gif"];
      if (!validTypes.includes(file.type)) {
        message.error("Please upload a valid image file (PNG, JPG, or GIF).");
        return;
      }
      setFormData((prev) => ({ ...prev, screenshot: file }));
    }
  };

  const handleFileSelect = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        message.error(
          "File size exceeds 10MB limit. Please choose a smaller file.",
        );
        return;
      }
      const validTypes = ["image/png", "image/jpeg", "image/gif"];
      if (!validTypes.includes(file.type)) {
        message.error("Please upload a valid image file (PNG, JPG, or GIF).");
        return;
      }
      setFormData((prev) => ({ ...prev, screenshot: file }));

      const previewUrl = URL.createObjectURL(file);
      setFilePreview(previewUrl);

      setFormData((prev) => ({ ...prev, screenshot: file }));
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      message.warning("Please enter your name.");
      return;
    }

    if (!formData.email.trim()) {
      message.warning("Please enter your email address.");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      message.warning("Please enter a valid email address.");
      return;
    }

    if (!formData.description.trim()) {
      message.warning("Please describe the issue or feedback.");
      return;
    }

    if (formData.screenshot) {
      const validTypes = ["image/png", "image/jpeg", "image/gif", "image/webp"];
      if (!validTypes.includes(formData.screenshot.type)) {
        message.error(
          "Please upload a valid image file (PNG, JPG, GIF, or WEBP).",
        );
        return;
      }

      if (formData.screenshot.size > 10 * 1024 * 1024) {
        message.error(
          "File size exceeds 10MB limit. Please choose a smaller file.",
        );
        return;
      }

      const isImage = formData.screenshot.type.startsWith("image/");
      if (!isImage) {
        message.error("File must be an image.");
        return;
      }
    }

    if (formData.screenshot && formData.screenshot.size > 5 * 1024 * 1024) {
      message.info("Large file detected. Upload may take a moment...");
    }

    submitBugReport(formData);
  };

  useEffect(() => {
    return () => {
      if (filePreview) {
        URL.revokeObjectURL(filePreview);
      }
    };
  }, [filePreview]);

  const handleCancel = () => {
    onClose();
    setShowUploadArea(false);
    setFormData({
      name: "",
      email: "",
      description: "",
      screenshot: null,
    });
    reset();
  };

  const handleRemoveFile = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setFormData((prev) => ({ ...prev, screenshot: null }));
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleUploadClick = () => {
    document.getElementById("screenshot-upload")?.click();
  };

  const handleAddScreenshotClick = () => {
    setShowUploadArea(true);
  };

  return (
    <>
      {isOpen && <div className="drawer-overlay" onClick={handleCancel} />}

      <div className={`bug-drawer ${isOpen ? "open" : ""}`}>
        <div
          className="drawer-header"
          style={{
            paddingBottom: "16px",
            marginBottom: "16px",
            marginLeft: 0,
            paddingLeft: 30,
            borderBottom: "none",
          }}
        >
          <h2
            style={{
              fontSize: "20px",
              fontWeight: 600,
              color: "#2563eb",
              margin: 0,
            }}
          >
            Share Your Feedback
          </h2>
          <p
            style={{
              fontSize: "14px",
              color: "#6b7280",
              marginTop: "6px",
              marginBottom: 0,
              lineHeight: "1.5",
            }}
          >
            Your feedback helps us build a better platform. We're all ears!
          </p>
        </div>

        <form onSubmit={handleSubmit} className="drawer-body">
          <div className="form-group">
            <label htmlFor="name">Name *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Enter your full name"
              disabled={isLoading}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">Email *</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Enter your email address"
              disabled={isLoading}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="description">Description *</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Describe your feedback, suggestion, or the issue you encountered"
              rows={4}
              disabled={isLoading}
              required
            />
          </div>

          <div className="form-group">
            {!showUploadArea ? (
              <button
                type="button"
                className="add-screenshot-btn"
                onClick={handleAddScreenshotClick}
                disabled={isLoading}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <rect x="3" y="3" width="18" height="18" rx="2" />
                  <circle cx="8.5" cy="8.5" r="1.5" />
                  <path d="M21 15L16 10L5 21" />
                </svg>
                Add a Screenshot
              </button>
            ) : (
              <>
                <label>Add a Screenshot</label>
                <div
                  className={`upload-area ${isDragging ? "dragging" : ""}`}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleFileDrop}
                  onClick={handleUploadClick}
                  style={{
                    cursor: isLoading ? "not-allowed" : "pointer",
                    opacity: isLoading ? 0.6 : 1,
                  }}
                >
                  {formData.screenshot ? (
                    <div className="file-preview-container">
                      <div className="file-preview">

                          {formData.screenshot.type.startsWith("image/") && (
                          <img
                            src={URL.createObjectURL(formData.screenshot)}
                            alt="Screenshot preview"
                            className="file-preview-image"
                            style={{
                              maxWidth: "100%",
                              maxHeight: "150px",
                              objectFit: "contain",
                              marginBottom: "8px",
                              borderRadius: "4px",
                            }}
                            onLoad={(e) => {
                              URL.revokeObjectURL(
                                (e.target as HTMLImageElement).src,
                              );
                            }}
                          />
                        )}
                        <div className="file-info">
                          <span className="file-name">
                            📎 {formData.screenshot.name}
                          </span>
                          <span
                            className="file-size"
                            style={{ fontSize: "12px", color: "#6b7280" }}
                          >
                            ({(formData.screenshot.size / 1024).toFixed(1)} KB)
                          </span>
                        </div>
                        <button
                          type="button"
                          className="remove-file"
                          onClick={handleRemoveFile}
                          disabled={isLoading}
                          style={{
                            background: "none",
                            border: "none",
                            color: "#ef4444",
                            fontSize: "20px",
                            cursor: isLoading ? "not-allowed" : "pointer",
                            padding: "0 8px",
                          }}
                        >
                          ×
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="upload-icon">
                        <svg
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <rect x="3" y="3" width="18" height="18" rx="2" />
                          <circle cx="8.5" cy="8.5" r="1.5" />
                          <path d="M21 15L16 10L5 21" />
                        </svg>
                      </div>
                      <span className="upload-text">
                        Click to upload or drag and drop
                      </span>
                      <span className="upload-hint">
                        PNG, JPG, GIF up to 10MB
                      </span>
                    </>
                  )}
                  <input
                    type="file"
                    id="screenshot-upload"
                    accept="image/png,image/jpeg,image/gif"
                    onChange={handleFileSelect}
                    style={{ display: "none" }}
                    disabled={isLoading}
                  />
                </div>
              </>
            )}
          </div>

          <div className="form-actions">
            <button type="submit" className="btn-submit" disabled={isLoading}>
              {isLoading ? "Submitting..." : "Submit"}
            </button>
            <button
              type="button"
              className="btn-cancel"
              onClick={handleCancel}
              disabled={isLoading}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export default ReportBugDrawer;
